/**
 * Support Details Panel
 * Sliding panel that displays detailed information about a support request
 */

import './ScenarioDetailsPanel.css';
import { apiFetch as baseApiFetch } from '../utils/apiClient.js';
import { escapeHtml } from '../utils/html.js';
import { createDeleteConfirmModal } from './ui/DeleteConfirmModal.js';

const SUPPORT_STATUS_CONFIG = {
  new: { text: 'Nouveau', color: '#b7791f', bg: 'rgba(255, 165, 0, 0.1)', border: 'rgba(255, 165, 0, 0.3)', icon: 'new_releases' },
  acknowledged: { text: 'Pris en charge', color: '#79AFBC', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'schedule' },
  waiting_user: {
    text: 'En attente réponse utilisateur',
    color: '#389798',
    bg: 'rgba(56, 151, 152, 0.1)',
    border: 'rgba(56, 151, 152, 0.3)',
    icon: 'reply'
  },
  waiting_support: { text: 'En attente réponse support', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'schedule' },
  resolved: { text: 'Résolu', color: '#389798', bg: 'rgba(46, 158, 106, 0.1)', border: 'rgba(46, 158, 106, 0.3)', icon: 'check_circle' },
  closed: { text: 'Fermé', color: '#508C94', bg: 'rgba(80, 140, 148, 0.1)', border: 'rgba(80, 140, 148, 0.3)', icon: 'lock' },
};

/**
 * Create support details panel
 * @param {Object} options
 * @param {Object} options.contact - Support contact to display
 * @param {Function} options.onClose - Callback when panel is closed
 * @param {Function} options.authFetch - Function to make authenticated API calls
 * @param {Function} options.onContactUpdated - Callback when contact is updated (for admin to reload list)
 * @returns {HTMLElement}
 */
export function createSupportDetailsPanel(options = {}) {
  const {
    contact,
    onClose,
    authFetch,
    onContactUpdated,
  } = options;

  const apiFetch = (path, options = {}) => {
    if (typeof authFetch === 'function') {
      return authFetch(path, options);
    }
    return baseApiFetch(path, options);
  };

  const container = document.createElement('div');

  // Overlay (backdrop)
  const overlay = document.createElement('div');
  overlay.className = 'details-panel-overlay';
  overlay.addEventListener('click', handleClose);
  container.appendChild(overlay);

  // Panel
  const panel = document.createElement('div');
  panel.className = 'details-panel';

  // Header
  const header = createHeader();
  panel.appendChild(header);

  // Content (scrollable)
  const content = document.createElement('div');
  content.className = 'details-panel-content';

  // Informations section (métadonnées)
  const infoSection = createInfoSection();
  content.appendChild(infoSection);

  // Timeline section (remplace toutes les sections de messages)
  const timelineSection = createTimelineSection();
  content.appendChild(timelineSection);

  // Reply form section
  const status = contact?.status || 'new';

  // Admin peut toujours répondre (sauf si fermé/résolu)
  if (status !== 'closed' && status !== 'resolved') {
    const adminReplyFormSection = createAdminReplyFormSection();
    content.appendChild(adminReplyFormSection);
  }

  panel.appendChild(content);

  // Footer admin (avec boutons Clôturer et Supprimer)
  const adminFooter = createAdminFooter();
  panel.appendChild(adminFooter);

  container.appendChild(panel);

  // Activate panel after a brief delay for animation
  setTimeout(() => {
    overlay.classList.add('active');
    panel.classList.add('active');
  }, 10);

  function createHeader() {
    const header = document.createElement('header');
    header.className = 'details-panel-header';

    const top = document.createElement('div');
    top.className = 'details-panel-header-top';
    top.style.cssText = 'display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;';

    const titleWrapper = document.createElement('div');
    titleWrapper.className = 'details-panel-title-wrapper';
    const title = 'Détails de la demande';
    const subtitle = 'Gestion de la demande de support';
    titleWrapper.innerHTML = `
      <span class="details-panel-icon material-symbols-outlined">support_agent</span>
      <div class="details-panel-title-group">
        <h2 class="details-panel-title">${escapeHtml(contact?.subject || title)}</h2>
        <p class="details-panel-subtitle">${subtitle}</p>
      </div>
    `;
    top.appendChild(titleWrapper);

    // Conteneur pour le statut et le bouton de fermeture (en haut à droite)
    const rightSection = document.createElement('div');
    rightSection.style.cssText = 'display: flex; align-items: center; gap: 1rem; flex-shrink: 0;';

    // Statut (badge en haut à droite)
    const status = contact?.status || 'new';
    const config = SUPPORT_STATUS_CONFIG[status] || SUPPORT_STATUS_CONFIG.new;

    const statusBadge = document.createElement('div');
    statusBadge.id = 'support-status-badge';
    statusBadge.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    statusBadge.innerHTML = `
      <span class="ad-pill" style="--ad-tone-fg: ${config.color}; --ad-tone-bg: ${config.bg};">${config.text}</span>
    `;
    rightSection.appendChild(statusBadge);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'details-panel-close-btn';
    closeBtn.innerHTML = '<span class="material-symbols-outlined">close</span>';
    closeBtn.addEventListener('click', handleClose);
    rightSection.appendChild(closeBtn);

    top.appendChild(rightSection);

    header.appendChild(top);

    // Ajouter les informations dans le header
    const headerInfo = document.createElement('div');
    headerInfo.style.cssText = 'display: flex; gap: 1.5rem; align-items: center; flex-wrap: wrap; margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(56, 151, 152, 0.1);';

    // Utilisateur (toujours affiché, même pour admin)
    const userInfo = document.createElement('div');
    userInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    const userName = contact?.userName || contact?.userEmail || 'Utilisateur';
    const userEmail = contact?.userEmail || '';
    userInfo.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">person</span>
      <div style="display: flex; flex-direction: column;">
        <span style="font-size: 0.875rem; color: var(--text-primary); font-weight: 500;">${escapeHtml(userName)}</span>
        ${userEmail ? `<span style="font-size: 0.75rem; color: rgba(80, 140, 148, 0.8);">${escapeHtml(userEmail)}</span>` : ''}
      </div>
    `;
    headerInfo.appendChild(userInfo);

    // Date de création
    const dateInfo = document.createElement('div');
    dateInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    const createdAt = contact?.createdAt ? new Date(contact.createdAt).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : 'Non défini';
    dateInfo.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">schedule</span>
      <span style="font-size: 0.875rem; color: var(--text-secondary);">${escapeHtml(createdAt)}</span>
    `;
    headerInfo.appendChild(dateInfo);

    // ID
    const idInfo = document.createElement('div');
    idInfo.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';
    const contactId = contact?.id || contact?._id || 'N/A';
    idInfo.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px; color: #508C94;">tag</span>
      <span style="font-size: 0.75rem; color: rgba(80, 140, 148, 0.8); font-family: monospace;">${escapeHtml(contactId.toString().slice(-8).toUpperCase())}</span>
    `;
    headerInfo.appendChild(idInfo);

    header.appendChild(headerInfo);

    return header;
  }


  function createInfoSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">info</span>
        Informations
      </h4>
    `;
    section.appendChild(sectionHeader);

    const infoGrid = document.createElement('div');
    infoGrid.style.cssText = 'display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;';

    // Date de création
    const createdAt = contact?.createdAt ? new Date(contact.createdAt).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : 'Non défini';

    // Date de mise à jour
    const updatedAt = contact?.updatedAt ? new Date(contact.updatedAt).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : 'Non défini';

    // Date de résolution (si résolu/fermé)
    let resolvedAt = null;
    if (contact?.resolvedAt) {
      resolvedAt = new Date(contact.resolvedAt).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }

    // ID
    const contactId = contact?.id || contact?._id || 'N/A';

    const infoItems = [
      { icon: 'schedule', label: 'Créé le', value: createdAt },
      { icon: 'update', label: 'Modifié le', value: updatedAt },
      { icon: 'tag', label: 'ID', value: contactId.toString().slice(-8).toUpperCase() },
    ];

    if (resolvedAt) {
      infoItems.push({ icon: 'check_circle', label: 'Résolu le', value: resolvedAt });
    }

    infoItems.forEach(item => {
      const itemDiv = document.createElement('div');
      itemDiv.style.cssText = 'padding: 0.75rem; background: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 0.5rem;';
      itemDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem;">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #508C94;">${item.icon}</span>
          <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">${item.label}</span>
        </div>
        <div style="font-size: 0.875rem; color: var(--text-primary); font-family: ${item.icon === 'tag' ? 'monospace' : 'inherit'};">
          ${escapeHtml(item.value)}
        </div>
      `;
      infoGrid.appendChild(itemDiv);
    });

    section.appendChild(infoGrid);
    return section;
  }

  function createTimelineSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">history</span>
        Chronologie
      </h4>
    `;
    section.appendChild(sectionHeader);

    // Créer un conteneur pour la timeline
    const timelineContainer = document.createElement('div');
    timelineContainer.style.cssText = 'position: relative; padding-left: 80px;';

    // Créer la ligne verticale de la timeline
    const timelineLine = document.createElement('div');
    timelineLine.style.cssText = 'position: absolute; left: 40px; top: 0; bottom: 0; width: 2px; background: var(--border-primary); z-index: 1;';
    timelineContainer.appendChild(timelineLine);

    // Créer un conteneur pour les items
    const itemsContainer = document.createElement('div');
    itemsContainer.style.cssText = 'position: relative; z-index: 2; display: flex; flex-direction: column; gap: 1.5rem;';

    // Collecter tous les événements dans l'ordre chronologique
    const events = [];

    // Message initial (toujours en premier)
    if (contact?.createdAt) {
      events.push({
        type: 'initial',
        date: new Date(contact.createdAt),
        subject: contact.subject,
        message: contact.message,
        order: 1, // Ordre logique pour le tri
      });
    }

    // Réponse du support
    if (contact?.adminNotes && contact.adminNotes.trim().length > 0) {
      // Pour la réponse du support, on utilise une heuristique :
      // - Si updatedAt est proche de createdAt, c'est probablement la première réponse
      // - Sinon, on utilise updatedAt mais on s'assure qu'il est après le message initial
      const createdAt = contact.createdAt ? new Date(contact.createdAt) : new Date();
      const updatedAt = contact.updatedAt ? new Date(contact.updatedAt) : createdAt;
      
      // Si updatedAt est très proche de createdAt (moins de 1 minute), c'est probablement la création
      // Sinon, on utilise updatedAt mais on s'assure qu'il est au moins après createdAt
      const supportDate = updatedAt.getTime() - createdAt.getTime() < 60000 ? 
        new Date(createdAt.getTime() + 1000) : // 1 seconde après la création
        updatedAt;
      
      events.push({
        type: 'support',
        date: supportDate,
        message: contact.adminNotes,
        order: 2, // Ordre logique : après le message initial
      });
    }

    // Réponses de l'utilisateur
    if (contact?.userReplies && Array.isArray(contact.userReplies) && contact.userReplies.length > 0) {
      contact.userReplies.forEach((reply) => {
        events.push({
          type: 'user',
          date: reply.createdAt ? new Date(reply.createdAt) : new Date(),
          message: reply.message,
          order: 3, // Ordre logique : après la réponse du support
        });
      });
    }

    // Trier par date, puis par ordre logique en cas d'égalité
    events.sort((a, b) => {
      const dateDiff = a.date - b.date;
      if (Math.abs(dateDiff) < 60000) { // Si les dates sont très proches (moins d'1 minute)
        return (a.order || 999) - (b.order || 999); // Utiliser l'ordre logique
      }
      return dateDiff; // Sinon, utiliser la date
    });

    // Créer les items de timeline
    events.forEach((event, index) => {
      const item = document.createElement('div');
      item.style.cssText = 'position: relative; display: flex; align-items: flex-start; gap: 1rem;';

      // Marker de la timeline
      const marker = document.createElement('div');
      marker.style.cssText = 'position: absolute; left: -80px; width: 60px; display: flex; align-items: center; justify-content: flex-end; padding-right: 1rem;';

      const dot = document.createElement('div');
      dot.style.cssText = 'width: 12px; height: 12px; border-radius: 50%; border: 2px solid var(--border-primary); flex-shrink: 0; z-index: 3;';

      if (event.type === 'initial') {
        dot.style.background = 'rgba(56, 151, 152, 1)';
        dot.style.boxShadow = '0 0 10px rgba(56, 151, 152, 0.5)';
      } else if (event.type === 'support') {
        dot.style.background = 'rgba(56, 151, 152, 1)';
        dot.style.boxShadow = '0 0 10px rgba(56, 151, 152, 0.5)';
      } else {
        dot.style.background = 'rgba(46, 158, 106, 1)';
        dot.style.boxShadow = '0 0 10px rgba(46, 158, 106, 0.5)';
      }

      marker.appendChild(dot);
      item.appendChild(marker);

      // Content wrapper avec position relative pour la date/heure à droite
      const contentWrapper = document.createElement('div');
      contentWrapper.style.cssText = 'flex: 1; min-width: 0; position: relative; padding-right: 120px;';

      // Time avec date et heure à droite au-dessus de la card
      const time = document.createElement('div');
      time.style.cssText = 'position: absolute; right: 0; top: 0; display: flex; flex-direction: column; gap: 0.1rem; align-items: flex-end;';
      const dateText = document.createElement('span');
      dateText.style.cssText = 'font-family: "Roboto Mono", monospace; font-size: 0.65rem; color: rgba(80, 140, 148, 0.8);  line-height: 1.2; white-space: nowrap;';
      const timeText = document.createElement('span');
      timeText.style.cssText = 'font-family: "Roboto Mono", monospace; font-size: 0.65rem; color: rgba(80, 140, 148, 0.8);  line-height: 1.2; white-space: nowrap;';
      const dateStr = event.date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      const timeStr = event.date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      dateText.textContent = dateStr;
      timeText.textContent = timeStr;
      time.appendChild(dateText);
      time.appendChild(timeText);
      contentWrapper.appendChild(time);

      // Content
      const content = document.createElement('div');
      content.style.cssText = 'flex: 1; min-width: 0;';

      let contentHtml = '';
      if (event.type === 'initial') {
        contentHtml = `
          <div style="padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
              <span class="material-symbols-outlined" style="font-size: 18px; color: #79AFBC;">support_agent</span>
              <span style="font-size: 0.75rem; color: #508C94;  font-weight: 600;">Demande initiale</span>
            </div>
            <div style="margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-primary);">
              <div style="font-size: 0.75rem; color: #508C94;  margin-bottom: 0.5rem; font-weight: 600;">Sujet</div>
              <div style="font-size: 1rem; color: var(--text-primary); font-weight: 600; line-height: 1.5;">${escapeHtml(event.subject || 'Aucun sujet')}</div>
            </div>
            <div>
              <div style="font-size: 0.75rem; color: #508C94;  margin-bottom: 0.5rem; font-weight: 600;">Message</div>
              <div style="color: var(--text-primary); line-height: 1.6; white-space: pre-wrap;">${escapeHtml(event.message || 'Aucun message')}</div>
            </div>
          </div>
        `;
      } else if (event.type === 'support') {
        contentHtml = `
          <div style="padding: 1.25rem; background: rgba(56, 151, 152, 0.1); border: 1px solid rgba(56, 151, 152, 0.3); border-radius: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
              <span class="material-symbols-outlined" style="font-size: 18px; color: #79AFBC;">reply</span>
              <span style="font-size: 0.75rem; color: #79AFBC;  font-weight: 600;">Réponse du support</span>
            </div>
            <div style="color: #389798; line-height: 1.6; white-space: pre-wrap;">${escapeHtml(event.message)}</div>
          </div>
        `;
      } else {
        contentHtml = `
          <div style="padding: 1.25rem; background: rgba(46, 158, 106, 0.1); border: 1px solid rgba(46, 158, 106, 0.3); border-radius: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;">
              <span class="material-symbols-outlined" style="font-size: 18px; color: #389798;">chat_bubble</span>
              <span style="font-size: 0.75rem; color: #389798;  font-weight: 600;">Votre réponse</span>
            </div>
            <div style="color: #389798; line-height: 1.6; white-space: pre-wrap;">${escapeHtml(event.message)}</div>
          </div>
        `;
      }

      content.innerHTML = contentHtml;
      contentWrapper.appendChild(content);
      item.appendChild(contentWrapper);
      itemsContainer.appendChild(item);
    });

    timelineContainer.appendChild(itemsContainer);
    section.appendChild(timelineContainer);

    return section;
  }

  function createAdminReplyFormSection() {
    const section = document.createElement('section');
    section.className = 'details-panel-section';
    section.id = 'support-admin-reply-form-section';

    const sectionHeader = document.createElement('div');
    sectionHeader.className = 'details-panel-section-header';
    sectionHeader.innerHTML = `
      <h4 class="details-panel-section-title">
        <span class="material-symbols-outlined" style="font-size: 18px; vertical-align: middle; margin-right: 0.5rem;">reply</span>
        Répondre en tant que support
      </h4>
    `;
    section.appendChild(sectionHeader);

    const formContainer = document.createElement('div');
    formContainer.style.cssText = 'padding: 1.25rem; background: var(--bg-secondary); border: 1px solid var(--border-primary); border-radius: 0.5rem;';

    const feedback = document.createElement('div');
    feedback.id = 'support-admin-reply-feedback';
    feedback.style.cssText = 'display: none; padding: 0.75rem 1rem; margin-bottom: 1rem; border-radius: 0.5rem; font-size: 0.875rem;';
    formContainer.appendChild(feedback);

    const textarea = document.createElement('textarea');
    textarea.id = 'support-admin-reply-message';
    textarea.placeholder = 'Tapez votre réponse ici...';
    textarea.maxLength = 5000;
    textarea.rows = 6;
    textarea.value = contact?.adminNotes || '';
    textarea.style.cssText = 'width: 100%; padding: 1rem; background: var(--bg-input); border: 2px solid var(--border-primary); border-radius: 0.5rem; color: var(--text-primary); font-family: inherit; font-size: 0.95rem; line-height: 1.6; resize: vertical; min-height: 120px;';
    formContainer.appendChild(textarea);

    const charCount = document.createElement('div');
    charCount.id = 'support-admin-reply-char-count';
    charCount.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin-top: 0.75rem; margin-bottom: 1rem;';
    charCount.innerHTML = `
      <span style="font-size: 0.875rem; color: #508C94;">Maximum 5000 caractères</span>
      <span id="support-admin-reply-char-count-value" style="font-size: 0.875rem; color: #508C94; font-weight: 500;">${(contact?.adminNotes || '').length} / 5000</span>
    `;
    formContainer.appendChild(charCount);

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = 'display: flex; gap: 1rem; justify-content: flex-end;';

    const submitBtn = document.createElement('button');
    submitBtn.id = 'support-admin-reply-submit';
    submitBtn.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px;">send</span>
      <span>Envoyer la réponse</span>
    `;
    submitBtn.classList.add('rd-btn', 'rd-btn-night');

    submitBtn.addEventListener('click', async () => {
      const message = textarea.value.trim();
      // Utiliser le statut actuel du contact (le statut peut être changé via le bouton "Changer le statut")
      const status = contact?.status || 'new';

      if (!message) {
        showAdminFeedback('Veuillez saisir un message.', 'error');
        return;
      }

      if (message.length > 5000) {
        showAdminFeedback('Le message ne peut pas dépasser 5000 caractères.', 'error');
        return;
      }

      submitBtn.disabled = true;
      const originalHtml = submitBtn.innerHTML;
      submitBtn.innerHTML = '<span class="loading-spinner-small"></span><span>Envoi...</span>';

      try {
        const response = await apiFetch(`/api/support/admin/${contact.id || contact._id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, adminNotes: message }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Erreur lors de l\'envoi de la réponse');
        }

        const data = await response.json();

        // Mettre à jour le contact avec les nouvelles données
        contact.status = data.status;
        contact.adminNotes = data.adminNotes;
        contact.updatedAt = data.updatedAt;

        // Afficher la belle modal de succès
        if (typeof window.showSuccessModal === 'function') {
          window.showSuccessModal('Votre réponse a été envoyée avec succès. L\'utilisateur recevra une notification.', 'Réponse envoyée', () => {
            // Recharger le contact depuis l'API pour avoir les données à jour
            if (onContactUpdated) {
              onContactUpdated(contact);
            }
            // Fermer et recharger la liste
            handleClose();
          });
        } else {
          // Fallback: utiliser showFeedback
          showAdminFeedback('Votre réponse a été envoyée avec succès.', 'success');
          setTimeout(() => {
            if (onContactUpdated) {
              onContactUpdated(contact);
            }
            handleClose();
          }, 2000);
        }
      } catch (error) {
        showAdminFeedback(error.message || 'Erreur lors de l\'envoi de la réponse. Veuillez réessayer.', 'error');
      } finally {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalHtml;
      }
    });

    buttonContainer.appendChild(submitBtn);
    formContainer.appendChild(buttonContainer);

    // Event listener pour le compteur de caractères
    textarea.addEventListener('input', () => {
      updateAdminCharCount();
    });

    function updateAdminCharCount() {
      const count = textarea.value.length;
      const countEl = formContainer.querySelector('#support-admin-reply-char-count-value');
      if (countEl) {
        countEl.textContent = `${count} / 5000`;
        if (count > 5000) {
          countEl.style.color = '#ff5555';
        } else {
          countEl.style.color = '#508C94';
        }
      }
    }

    function showAdminFeedback(message, type) {
      feedback.textContent = message;
      feedback.style.display = 'block';
      if (type === 'success') {
        feedback.style.background = 'rgba(46, 158, 106, 0.1)';
        feedback.style.border = '1px solid rgba(46, 158, 106, 0.3)';
        feedback.style.color = '#389798';
      } else {
        feedback.style.background = 'rgba(239, 68, 68, 0.1)';
        feedback.style.border = '1px solid rgba(239, 68, 68, 0.3)';
        feedback.style.color = '#c9453a';
      }
    }

    section.appendChild(formContainer);
    return section;
  }

  function createAdminFooter() {
    const footer = document.createElement('footer');
    footer.className = 'details-panel-footer';

    const actions = document.createElement('div');
    actions.className = 'details-panel-actions';
    actions.style.cssText = 'display: flex; gap: 1rem; justify-content: flex-end;';

    const status = contact?.status || 'new';

    // Bouton Clôturer (seulement si pas déjà fermé)
    if (status !== 'closed') {
      const closeBtn = document.createElement('button');
      closeBtn.id = 'support-admin-close-ticket-btn';
      closeBtn.className = 'action-main-btn action-close';
      closeBtn.innerHTML = `
        <span class="material-symbols-outlined" style="font-size: 18px;">lock</span>
        <span>Clôturer</span>
      `;

      closeBtn.addEventListener('click', async () => {
        showAdminCloseTicketConfirmationModal(async () => {
          closeBtn.disabled = true;
          const originalHtml = closeBtn.innerHTML;
          closeBtn.innerHTML = '<span class="loading-spinner-small"></span><span>Fermeture…</span>';

          try {
            const response = await apiFetch(`/api/support/admin/${contact.id || contact._id}/status`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'closed', adminNotes: contact?.adminNotes || '' }),
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.error || 'Erreur lors de la fermeture du ticket');
            }

            const data = await response.json();

            // Mettre à jour le contact
            contact.status = data.status;
            contact.closedAt = data.closedAt;

            // Afficher la belle modal de succès
            if (typeof window.showSuccessModal === 'function') {
              window.showSuccessModal('Le ticket a été clôturé avec succès.', 'Ticket clôturé', () => {
                if (onContactUpdated) {
                  onContactUpdated(contact);
                }
                handleClose();
              });
            } else {
              if (typeof window.showGlobalNotification === 'function') {
                window.showGlobalNotification('success', 'Le ticket a été clôturé avec succès.');
              }
              setTimeout(() => {
                if (onContactUpdated) {
                  onContactUpdated(contact);
                }
                handleClose();
              }, 2000);
            }
          } catch (error) {
            if (typeof window.showGlobalNotification === 'function') {
              window.showGlobalNotification('error', error.message || 'Erreur lors de la fermeture du ticket.');
            }
            closeBtn.disabled = false;
            closeBtn.innerHTML = originalHtml;
          }
        });
      });

      actions.appendChild(closeBtn);
    }

    // Bouton Changer le statut (toujours disponible pour admin)
    const changeStatusBtn = document.createElement('button');
    changeStatusBtn.id = 'support-admin-change-status-btn';
    changeStatusBtn.className = 'action-main-btn action-secondary';
    changeStatusBtn.classList.add('rd-btn', 'rd-btn-light');
    changeStatusBtn.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px;">swap_horiz</span>
      <span>Changer le statut</span>
    `;

    changeStatusBtn.addEventListener('click', () => {
      showChangeStatusModal();
    });

    actions.appendChild(changeStatusBtn);

    // Bouton Supprimer (toujours disponible pour admin)
    const deleteBtn = document.createElement('button');
    deleteBtn.id = 'support-admin-delete-ticket-btn';
    deleteBtn.className = 'action-main-btn action-delete';
    deleteBtn.classList.add('rd-btn', 'ad-btn-danger');
    deleteBtn.innerHTML = `
      <span class="material-symbols-outlined" style="font-size: 18px;">delete</span>
      <span>Supprimer</span>
    `;

    deleteBtn.addEventListener('click', async () => {
      showAdminDeleteTicketConfirmationModal(async () => {
        deleteBtn.disabled = true;
        const originalHtml = deleteBtn.innerHTML;
        deleteBtn.innerHTML = '<span class="loading-spinner-small"></span><span>Suppression…</span>';

        try {
          const response = await apiFetch(`/api/support/admin/${contact.id || contact._id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erreur lors de la suppression du ticket');
          }

          // Afficher la belle modal de succès
          if (typeof window.showSuccessModal === 'function') {
            window.showSuccessModal('Le ticket a été supprimé avec succès.', 'Ticket supprimé', () => {
              if (onContactUpdated) {
                onContactUpdated(contact);
              }
              handleClose();
            });
          } else {
            if (typeof window.showGlobalNotification === 'function') {
              window.showGlobalNotification('success', 'Le ticket a été supprimé avec succès.');
            }
            setTimeout(() => {
              if (onContactUpdated) {
                onContactUpdated(contact);
              }
              handleClose();
            }, 2000);
          }
        } catch (error) {
          if (typeof window.showGlobalNotification === 'function') {
            window.showGlobalNotification('error', error.message || 'Erreur lors de la suppression du ticket.');
          }
          deleteBtn.disabled = false;
          deleteBtn.innerHTML = originalHtml;
        }
      });
    });

    actions.appendChild(deleteBtn);
    footer.appendChild(actions);

    return footer;
  }

  function showAdminCloseTicketConfirmationModal(onConfirm) {
    const modal = document.createElement('div');
    modal.className = 'modal modal-validate-confirm';
    modal.style.cssText = 'display: flex; position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.7); backdrop-filter: blur(8px); align-items: center; justify-content: center; z-index: 3000;';

    modal.innerHTML = `
      <div class="modal-content modal-validate-content" style="max-width: 520px; width: 90%;">
        <button class="modal-close" aria-label="Fermer">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="modal-validate-body">
          <div class="modal-validate-mouse">
            <img src="/images/souris/VEILLE.png" alt="Souris" class="mouse-animation" />
          </div>
          <div class="modal-validate-content-wrapper">
            <h2 class="modal-validate-title">Clôturer le ticket</h2>
            <p class="modal-validate-text">
              Êtes-vous sûr de vouloir clôturer ce ticket ? Cette action est <strong>définitive</strong> et l'utilisateur ne pourra plus répondre à ce ticket.
            </p>
          </div>
        </div>
        <div class="modal-validate-footer">
          <button class="modal-validate-btn modal-validate-btn-cancel" id="admin-close-ticket-cancel">
            <span>Annuler</span>
          </button>
          <button class="modal-validate-btn modal-validate-btn-confirm" id="admin-close-ticket-confirm">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>Clôturer</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Animation d'entrée
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);

    // Fonction de fermeture
    const closeModal = () => {
      modal.classList.remove('active');
      setTimeout(() => {
        if (document.body.contains(modal)) {
          modal.remove();
        }
      }, 300);
    };

    // Event listeners
    const closeBtn = modal.querySelector('.modal-close');
    const cancelBtn = modal.querySelector('#admin-close-ticket-cancel');
    const confirmBtn = modal.querySelector('#admin-close-ticket-confirm');

    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);
    confirmBtn?.addEventListener('click', () => {
      closeModal();
      if (onConfirm) {
        onConfirm();
      }
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    return modal;
  }

  function showAdminDeleteTicketConfirmationModal(onConfirm) {
    const deleteModal = createDeleteConfirmModal({
      title: 'Supprimer le ticket',
      message: 'Êtes-vous sûr de vouloir <strong>supprimer définitivement</strong> ce ticket ?',
      warningMessage: 'Cette action est <strong>irréversible</strong> et toutes les données associées seront perdues.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      onConfirm: onConfirm,
    });
    
    deleteModal.show();
  }

  function showChangeStatusModal() {
    const modal = document.createElement('div');
    modal.className = 'modal modal-validate-confirm';
    modal.style.cssText = 'display: flex; position: fixed; inset: 0; background-color: rgba(0, 0, 0, 0.7); backdrop-filter: blur(8px); align-items: center; justify-content: center; z-index: 3000;';

    const currentStatus = contact?.status || 'new';
    const statusOptions = [
      { value: 'new', label: 'Nouveau', color: '#b7791f', bg: 'rgba(255, 165, 0, 0.1)', border: 'rgba(255, 165, 0, 0.3)', icon: 'new_releases' },
      { value: 'acknowledged', label: 'Pris en charge', color: '#79AFBC', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'schedule' },
      { value: 'waiting_user', label: 'En attente réponse utilisateur', color: '#389798', bg: 'rgba(56, 151, 152, 0.1)', border: 'rgba(56, 151, 152, 0.3)', icon: 'reply' },
      { value: 'waiting_support', label: 'En attente réponse support', color: '#b7791f', bg: 'rgba(245, 158, 11, 0.1)', border: 'rgba(245, 158, 11, 0.3)', icon: 'schedule' },
      { value: 'resolved', label: 'Résolu', color: '#389798', bg: 'rgba(46, 158, 106, 0.1)', border: 'rgba(46, 158, 106, 0.3)', icon: 'check_circle' },
      { value: 'closed', label: 'Fermé', color: '#508C94', bg: 'rgba(80, 140, 148, 0.1)', border: 'rgba(80, 140, 148, 0.3)', icon: 'lock' },
    ];

    let selectedStatus = currentStatus;

    modal.innerHTML = `
      <div class="modal-content modal-validate-content" style="max-width: 520px; width: 90%;">
        <div class="modal-validate-body">
          <div class="modal-validate-content-wrapper">
            <h2 class="modal-validate-title">
              Changer le statut du ticket
            </h2>
            <div style="margin-bottom: 1.5rem;">
              <label class="ad-label" style="display: block; margin-bottom: 0.75rem;">
                Sélectionner un statut
              </label>
              <div style="display: flex; flex-direction: column; gap: 0.75rem;">
                ${statusOptions.map(opt => {
      const isSelected = opt.value === currentStatus;
      return `
                    <button 
                      type="button"
                      class="status-option-btn" 
                      data-status="${opt.value}"
                      ${isSelected ? 'data-selected="true"' : ''}
                      style="
                        width: 100%;
                        padding: 1rem 1.25rem;
                        background: ${isSelected ? opt.bg : 'rgba(56, 151, 152, 0.05)'};
                        border: 2px solid ${isSelected ? opt.border : 'rgba(56, 151, 152, 0.2)'};
                        border-radius: 0.5rem;
                        color: ${opt.color};
                        font-size: 0.95rem;
                        font-family: 'Barlow', sans-serif;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        display: flex;
                        align-items: center;
                        gap: 1rem;
                        text-align: left;
                        outline: none;
                        ${isSelected ? `box-shadow: 0 0 20px ${opt.color}40;` : ''}
                      "
                    >
                      <span class="material-symbols-outlined" style="font-size: 24px; color: ${opt.color}; flex-shrink: 0;">
                        ${opt.icon}
                      </span>
                      <div style="flex: 1;">
                        <div style="font-size: 1rem; font-weight: 600; margin-bottom: 0.25rem;">${escapeHtml(opt.label)}</div>
                        ${isSelected ? '<div style="font-size: 0.75rem; opacity: 0.8;">Statut actuel</div>' : ''}
                      </div>
                      ${isSelected ? `
                        <span class="material-symbols-outlined" style="font-size: 20px; color: ${opt.color}; flex-shrink: 0;">
                          check_circle
                        </span>
                      ` : ''}
                    </button>
                  `;
    }).join('')}
              </div>
            </div>
          </div>
        </div>
        <div class="modal-validate-footer" style="display: flex; gap: 0.75rem;">
          <button class="modal-validate-btn modal-validate-btn-cancel">
            <span>Annuler</span>
          </button>
          <button class="modal-validate-btn modal-validate-btn-confirm">
            <span class="material-symbols-outlined" style="font-size: 18px;">check</span>
            <span>Confirmer</span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Animation d'entrée
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);

    const statusButtons = modal.querySelectorAll('.status-option-btn');
    const confirmBtn = modal.querySelector('.modal-validate-btn-confirm');
    const cancelBtn = modal.querySelector('.modal-validate-btn-cancel');

    // Gérer la sélection des statuts
    statusButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        // Désélectionner tous les boutons
        statusButtons.forEach(b => {
          b.style.background = 'rgba(56, 151, 152, 0.05)';
          b.style.borderColor = 'rgba(56, 151, 152, 0.2)';
          b.style.boxShadow = 'none';
          b.dataset.selected = 'false';
          const checkIcon = b.querySelector('.material-symbols-outlined:last-child');
          if (checkIcon && checkIcon.textContent === 'check_circle') {
            checkIcon.remove();
          }
          const statusText = b.querySelector('div > div:last-child');
          if (statusText && statusText.textContent === 'Statut actuel') {
            statusText.remove();
          }
        });

        // Sélectionner le bouton cliqué
        const statusValue = btn.dataset.status;
        const opt = statusOptions.find(o => o.value === statusValue);
        selectedStatus = statusValue;
        btn.style.background = opt.bg;
        btn.style.borderColor = opt.border;
        btn.style.boxShadow = `0 0 20px ${opt.color}40`;
        btn.dataset.selected = 'true';

        // Ajouter l'icône de check si ce n'est pas le statut actuel
        if (statusValue !== currentStatus) {
          const checkIcon = document.createElement('span');
          checkIcon.className = 'material-symbols-outlined';
          checkIcon.style.cssText = `font-size: 20px; color: ${opt.color}; flex-shrink: 0;`;
          checkIcon.textContent = 'check_circle';
          btn.appendChild(checkIcon);
        }
      });
    });

    const closeModal = () => {
      modal.classList.remove('active');
      setTimeout(() => {
        modal.remove();
      }, 300);
    };

    cancelBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal();
      }
    });

    confirmBtn.addEventListener('click', async () => {
      if (selectedStatus === currentStatus) {
        closeModal();
        return;
      }

      confirmBtn.disabled = true;
      const originalHtml = confirmBtn.innerHTML;
      confirmBtn.innerHTML = '<span class="loading-spinner-small"></span><span>Mise à jour...</span>';

      try {
        const contactId = contact?.id || contact?._id;
        if (!contactId) {
          throw new Error('ID de ticket manquant');
        }

        const response = await apiFetch(`/api/support/admin/${contactId}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: selectedStatus, adminNotes: contact?.adminNotes || '' }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.error || 'Erreur lors de la mise à jour du statut');
        }

        const data = await response.json();

        // Mettre à jour le statut dans l'objet contact
        contact.status = data.status;
        contact.updatedAt = data.updatedAt;

        // Mettre à jour le badge de statut dans le header
        const statusBadge = panel.querySelector('#support-status-badge');
        if (statusBadge) {
          const config = SUPPORT_STATUS_CONFIG[data.status] || SUPPORT_STATUS_CONFIG.new;
          statusBadge.innerHTML = `
            <span class="ad-pill" style="--ad-tone-fg: ${config.color}; --ad-tone-bg: ${config.bg};">${config.text}</span>
          `;
        }

        // Le statut est maintenant géré via le bouton "Changer le statut", pas besoin de mettre à jour un select

        // Callback pour recharger la liste si fourni
        if (onContactUpdated && typeof onContactUpdated === 'function') {
          onContactUpdated(contact);
        }

        closeModal();

        if (typeof window.showSuccessModal === 'function') {
          window.showSuccessModal('Le statut du ticket a été mis à jour avec succès.', 'Statut mis à jour');
        } else if (typeof window.showGlobalNotification === 'function') {
          window.showGlobalNotification('success', 'Le statut du ticket a été mis à jour avec succès.');
        }
      } catch (error) {
        console.error('Error updating status', error);
        if (typeof window.showGlobalNotification === 'function') {
          window.showGlobalNotification('error', error.message || 'Erreur lors de la mise à jour du statut');
        } else {
          alert(error.message || 'Erreur lors de la mise à jour du statut');
        }
        confirmBtn.disabled = false;
        confirmBtn.innerHTML = originalHtml;
      }
    });

    // Fermer avec Escape
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
  }

  function handleClose() {
    overlay.classList.remove('active');
    panel.classList.remove('active');
    setTimeout(() => {
      container.remove();
      if (onClose) {
        onClose();
      }
    }, 300);
  }

  return container;
}

